import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  AllocateApiOrDedicatedCliRequest,
  AllocateCliRequest,
  ArchivedCliRow,
  AvailableCliRow,
  ChangeCliRequest,
  CliDetailRow,
  CliRow,
  CliType,
  DeallocateCliRequest,
  DialerCliRow,
  DialerNameRow,
  PilotNumberRow,
  RemoveApiOrDedicatedCliRequest,
  TotalCliUsageRequest,
  TotalCliUsageRow,
  UnarchiveCliRequest,
  UpdateCliRequest,
  UploadCliFilePayload,
  UserPickerRow,
} from './types';

export const cliKeys = {
  all: ['cli'] as const,
  list: (userId: string | number, cliType: CliType) =>
    ['cli', 'list', userId, cliType] as const,
  apiList: (userId: string | number) => ['cli', 'api-list', userId] as const,
  dialerCliList: ['cli', 'dialer-cli-list'] as const,
  available: (dialerId: string | number) =>
    ['cli', 'available', dialerId] as const,
  pilotList: ['cli', 'pilot-list'] as const,
  dialerNames: ['cli', 'dialer-names'] as const,
  pilotSearch: (pilotNumber: string) => ['cli', 'pilot-search', pilotNumber] as const,
  archived: ['cli', 'archived'] as const,
  archivedApi: ['cli', 'archived-api'] as const,
  userArchived: (userId: string | number) =>
    ['cli', 'user-archived', userId] as const,
};

function useInvalidateCli() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: cliKeys.all });
}

/* ---------------- Main listing ---------------- */

/**
 * Main CLI list used by /cli-list. SADMIN can toggle between the two
 * cliTypes; SUB_SUPER_ADMIN sees `cli`, ADMIN sees `dedicated_cli`.
 */
export function useCliList(cliType: CliType, enabled = true) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: cliKeys.list(userId ?? 'none', cliType),
    queryFn: () => {
      const endpoint =
        cliType === 'cli' ? '/api/obd/cli/' : '/api/obd/cli/dedicated/';
      return api.get<CliRow[]>(`${endpoint}${userId ?? ''}`);
    },
    enabled: enabled && Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<CliRow>(data),
  });
}

export function useDeallocateCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (req: DeallocateCliRequest) =>
      apiClient
        .delete<{ message?: string }>('/api/obd/cli', { data: req })
        .then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}

export function useBulkDeleteCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    /** Body: `{ filterData }` — comma-joined list of cliIds. */
    mutationFn: (cliIds: number[]) =>
      api.post<{ message?: string }>('/api/obd/bulk/delete/cli', {
        filterData: cliIds.join(','),
      }),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- Allocate page ---------------- */

export function useDialerCliList() {
  return useQuery({
    queryKey: cliKeys.dialerCliList,
    queryFn: () => api.get<DialerCliRow[]>('/api/obd/dialers/cli/list'),
    staleTime: 30_000,
    select: (data) => ensureArray<DialerCliRow>(data),
  });
}

export function useAvailableCli(dialerId: string | number | null) {
  return useQuery({
    queryKey: cliKeys.available(dialerId ?? 'none'),
    queryFn: () =>
      api.get<AvailableCliRow[]>(`/api/obd/cli/available/${dialerId}`),
    enabled: Boolean(dialerId),
    staleTime: 10_000,
    select: (data) => ensureArray<AvailableCliRow>(data),
  });
}

export function useChangeCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (req: ChangeCliRequest) =>
      api.post<{ message?: string }>('/api/obd/change/cli', req),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- Upload page ---------------- */

export function useDialerNames() {
  return useQuery({
    queryKey: cliKeys.dialerNames,
    queryFn: () => api.get<DialerNameRow[]>('/api/obd/dialers/name/list'),
    staleTime: 60_000,
    select: (data) => ensureArray<DialerNameRow>(data),
  });
}

export function usePilotNumbers() {
  return useQuery({
    queryKey: cliKeys.pilotList,
    queryFn: () => api.get<PilotNumberRow[]>('/api/obd/pilot/list'),
    staleTime: 60_000,
    select: (data) => ensureArray<PilotNumberRow>(data),
  });
}

export function useUploadCliFile() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (payload: UploadCliFilePayload) => {
      const fd = new FormData();
      fd.append('cliFile', payload.cliFile);
      fd.append('userId', String(payload.userId));
      fd.append('pool', payload.pool);
      fd.append('dialerId', payload.dialerId);
      fd.append('pilotNumber', payload.pilotNumber);
      return apiClient
        .post<{ message?: string }>('/api/obd/cliupload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: () => invalidate(),
  });
}

/* ---------------- Pilot CLI search ---------------- */

/** GET /api/obd/cli/pilot/{pilotNumber} — manual search-on-demand. */
export function useFetchPilotCli() {
  return useMutation({
    mutationFn: (pilotNumber: string) =>
      api
        .get<CliRow[]>(`/api/obd/cli/pilot/${pilotNumber}`)
        .then((res) => ensureArray<CliRow>(res)),
  });
}

/* ---------------- Archived CLI ---------------- */

export function useArchivedCli() {
  return useQuery({
    queryKey: cliKeys.archived,
    queryFn: () => api.get<ArchivedCliRow[]>('/api/obd/cli/archive'),
    staleTime: 30_000,
    select: (data) => ensureArray<ArchivedCliRow>(data),
  });
}

export function useUserArchivedCli() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: cliKeys.userArchived(userId ?? 'none'),
    queryFn: () =>
      api.get<ArchivedCliRow[]>(`/api/obd/user/cli/archive/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<ArchivedCliRow>(data),
  });
}

export function useUnarchiveCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (req: UnarchiveCliRequest) =>
      api.post<{ message?: string }>('/api/obd/cli/archive', req),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- API CLI (sibling endpoints) ---------------- */

/** GET /api/obd/cli/api/{userId} — API CLI list. */
export function useApiCliList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: cliKeys.apiList(userId ?? 'none'),
    queryFn: () => api.get<CliRow[]>(`/api/obd/cli/api/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<CliRow>(data),
  });
}

/** POST /api/obd/api/cliupload — multipart upload for the API CLI pool. */
export function useUploadApiCliFile() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (payload: UploadCliFilePayload) => {
      const fd = new FormData();
      fd.append('cliFile', payload.cliFile);
      fd.append('dialerId', payload.dialerId);
      fd.append('userId', String(payload.userId));
      fd.append('pool', payload.pool);
      fd.append('pilotNumber', payload.pilotNumber);
      return apiClient
        .post<{ message?: string }>('/api/obd/api/cliupload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: () => invalidate(),
  });
}

/** GET /api/obd/cli/archive/api — archived API CLIs. */
export function useArchivedApiCli() {
  return useQuery({
    queryKey: cliKeys.archivedApi,
    queryFn: () => api.get<ArchivedCliRow[]>('/api/obd/cli/archive/api'),
    staleTime: 30_000,
    select: (data) => ensureArray<ArchivedCliRow>(data),
  });
}

/* ============================================================
 *  Dedicated CLI (sibling endpoints + archive + upload)
 * ============================================================ */

/** GET /api/obd/cli/archive/dedicated — archived dedicated CLIs. */
export function useArchivedDedicatedCli() {
  return useQuery({
    queryKey: ['cli', 'archived-dedicated'] as const,
    queryFn: () =>
      api.get<ArchivedCliRow[]>('/api/obd/cli/archive/dedicated'),
    staleTime: 30_000,
    select: (data) => ensureArray<ArchivedCliRow>(data),
  });
}

/** POST /api/obd/dedicated/cliupload — bulk upload to the dedicated pool. */
export function useUploadDedicatedCliFile() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (payload: UploadCliFilePayload) => {
      const fd = new FormData();
      fd.append('cliFile', payload.cliFile);
      fd.append('userId', String(payload.userId));
      fd.append('pool', payload.pool);
      fd.append('dialerId', payload.dialerId);
      fd.append('pilotNumber', payload.pilotNumber);
      return apiClient
        .post<{ message?: string }>('/api/obd/dedicated/cliupload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: () => invalidate(),
  });
}

/* ============================================================
 *  Allocate / Update / Remove flows
 * ============================================================ */

/** GET /api/obd/cli/all — every shared CLI on the platform (SADMIN). */
export function useAllSharedCli(enabled = true) {
  return useQuery({
    queryKey: ['cli', 'all'] as const,
    queryFn: () => api.get<CliRow[]>('/api/obd/cli/all'),
    enabled,
    staleTime: 30_000,
    select: (data) => ensureArray<CliRow>(data),
  });
}

/** GET /api/obd/cli/allocated/:userId — admin's own allocated CLI pool. */
export function useAllocatedCli(userId: number | string | null) {
  return useQuery({
    queryKey: ['cli', 'allocated', userId] as const,
    queryFn: () => api.get<CliRow[]>(`/api/obd/cli/allocated/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<CliRow>(data),
  });
}

/** GET /api/obd/cli/detail/:cliId — single CLI for the edit page. */
export function useCliDetail(cliId: number | string | null) {
  return useQuery({
    queryKey: ['cli', 'detail', cliId] as const,
    queryFn: () => api.get<CliDetailRow>(`/api/obd/cli/detail/${cliId}`),
    enabled: Boolean(cliId),
    staleTime: 30_000,
  });
}

/** GET /api/obd/users/:userId — reseller's user list, used as picker. */
export function useResellerUserPicker(enabled = true) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ['cli', 'reseller-users', userId] as const,
    queryFn: () => api.get<UserPickerRow[]>(`/api/obd/users/${userId}`),
    enabled: enabled && Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<UserPickerRow>(data),
  });
}

/** GET /api/obd/all/user/list/:userId — whole tree under the logged-in user (reseller picker). */
export function useTreeUserPicker(enabled = true) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ['cli', 'tree-users', userId] as const,
    queryFn: () => api.get<UserPickerRow[]>(`/api/obd/all/user/list/${userId}`),
    enabled: enabled && Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<UserPickerRow>(data),
  });
}

/** POST /api/obd/cli — bulk allocate a CSV list of CLIs to a user. */
export function useAllocateCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (body: AllocateCliRequest) =>
      api.post<{ message?: string }>('/api/obd/cli', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/cli/update — rename a CLI's value (e.g. correct a typo). */
export function useUpdateCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (body: UpdateCliRequest) =>
      api.post<{ message?: string }>('/api/obd/cli/update', body),
    onSuccess: () => invalidate(),
  });
}

/** GET /api/obd/api/cli/allocated/:userId — API CLIs currently held by a user. */
export function useAllocatedApiCli(userId: number | string | null) {
  return useQuery({
    queryKey: ['cli', 'api', 'allocated', userId] as const,
    queryFn: () => api.get<CliRow[]>(`/api/obd/api/cli/allocated/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<CliRow>(data),
  });
}

/** GET /api/obd/dedicated/cli/allocated/:userId — Dedicated CLIs held by a user. */
export function useAllocatedDedicatedCli(userId: number | string | null) {
  return useQuery({
    queryKey: ['cli', 'dedicated', 'allocated', userId] as const,
    queryFn: () =>
      api.get<CliRow[]>(`/api/obd/dedicated/cli/allocated/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<CliRow>(data),
  });
}

/** POST /api/obd/api/cli/allocate — assign CLIs to a user in the API pool. */
export function useAllocateApiCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (body: AllocateApiOrDedicatedCliRequest) =>
      api.post<{ message?: string }>('/api/obd/api/cli/allocate', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/api/remove/cli — deallocate API CLIs from a user. */
export function useRemoveApiCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (body: RemoveApiOrDedicatedCliRequest) =>
      api.post<{ message?: string }>('/api/obd/api/remove/cli', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/dedicated/cli/allocate — assign Dedicated CLIs to a user. */
export function useAllocateDedicatedCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (body: AllocateApiOrDedicatedCliRequest) =>
      api.post<{ message?: string }>('/api/obd/dedicated/cli/allocate', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/dedicated/remove/cli — deallocate Dedicated CLIs from a user. */
export function useRemoveDedicatedCli() {
  const invalidate = useInvalidateCli();
  return useMutation({
    mutationFn: (body: RemoveApiOrDedicatedCliRequest) =>
      api.post<{ message?: string }>('/api/obd/dedicated/remove/cli', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/total/cli/usage — CLI usage summary */
export function useTotalCliUsage() {
  return useMutation({
    mutationFn: (req: TotalCliUsageRequest) =>
      api.post<TotalCliUsageRow[]>('/api/obd/total/cli/usage', req),
  });
}
